import { useState, useRef, useEffect } from 'react';

const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};
import { VotingModal } from '../app/components/VotingModal';

const AnimatedBellIcon = ({ isAnimated, showVotingResults, ...props }: React.SVGProps<SVGSVGElement> & { isAnimated?: boolean, showVotingResults?: boolean }) => (
  <div style={{ position: 'relative', display: 'inline-block' }}>
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      {/* Колокольчик уведомлений */}
      <g className={isAnimated ? 'bell-ring-animation' : ''} style={{ transformOrigin: '12px 8px' }}>
        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </g>
    </svg>
    {/* Цифра 1 */}
    <div
      style={{
        position: 'absolute',
        top: '-2px',
        right: '-2px',
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        backgroundColor: '#ef4444',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '8px',
        fontWeight: 'bold',
        border: '1px solid #000000'
      }}
    >
      1
    </div>
    <style dangerouslySetInnerHTML={{
      __html: `
        .bell-ring-animation {
          animation: bellRing 1.2s ease-in-out;
        }
        @keyframes bellRing {
          0%, 100% { transform: rotate(0deg) scale(1); }
          10% { transform: rotate(-15deg) scale(1.1); }
          20% { transform: rotate(15deg) scale(1.1); }
          30% { transform: rotate(-10deg) scale(1.05); }
          40% { transform: rotate(10deg) scale(1.05); }
          50% { transform: rotate(-5deg) scale(1.02); }
          60% { transform: rotate(5deg) scale(1.02); }
          70% { transform: rotate(-2deg) scale(1.01); }
          80% { transform: rotate(2deg) scale(1.01); }
          90% { transform: rotate(0deg) scale(1.005); }
        }
      `
    }} />
  </div>
);

interface NewsDropdownProps {
  showVotingResults?: boolean;
}

export function NewsDropdown({ showVotingResults = false }: NewsDropdownProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMainButtonAnimated, setIsMainButtonAnimated] = useState(false);
  const [expandedNotification, setExpandedNotification] = useState<string | null>(null);
  const [isVotingModalOpen, setIsVotingModalOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { width } = useWindowSize();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setExpandedNotification(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleMainButtonClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
    if (!isDropdownOpen) {
      setIsMainButtonAnimated(true);
    } else {
      setExpandedNotification(null);
    }
  };

  const handleVotingModalClose = () => {
    setIsVotingModalOpen(false);
  };

  // Сброс анимаций при закрытии выпадающего списка
  useEffect(() => {
    if (!isDropdownOpen) {
      setIsMainButtonAnimated(false);
    }
  }, [isDropdownOpen]);

  const handleNotificationClick = (notificationId: string) => {
    if (expandedNotification === notificationId) {
      if (notificationId === 'tweet-contest') {
        window.open('https://x.com/arcanehandsxyz/status/1983645900300788091', '_blank');
        setIsDropdownOpen(false);
        setExpandedNotification(null);
      } else if (notificationId === 'weekly-vote') {
        setIsVotingModalOpen(true);
        setIsDropdownOpen(false);
        setExpandedNotification(null);
      }
    } else {
      setExpandedNotification(notificationId);
    }
  };


  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleMainButtonClick}
        onMouseEnter={() => setIsMainButtonAnimated(true)}
        onMouseLeave={() => !isDropdownOpen && setIsMainButtonAnimated(false)}
        className="flex items-center gap-1 sm:gap-1 btn-textured px-3 sm:px-4 md:px-5 py-2 sm:py-2 md:py-2.5 text-white text-xs sm:text-sm md:text-base border-2 border-purple-500/80 relative"
      >
        <AnimatedBellIcon isAnimated={isMainButtonAnimated} showVotingResults={showVotingResults} className="w-4 h-4 sm:w-5 sm:h-5" />
        <span className="mr-1">News</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}>
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>

      <div
        className="absolute mt-2 w-64 rounded-xl shadow-xl z-50 border-2 border-purple-800/50 overflow-hidden"
        style={{
          backgroundColor: '#000000',
          transform: isDropdownOpen
            ? (width >= 768 ? 'translateY(0) translateX(-50%)' : 'translateY(0)')
            : (width >= 768 ? 'translateY(-10px) translateX(-50%)' : 'translateY(-10px)'),
          transition: 'transform 0.15s ease-out, opacity 0.15s ease-out',
          opacity: isDropdownOpen ? 1 : 0,
          pointerEvents: isDropdownOpen ? 'auto' : 'none',
          right: width < 768 ? '0' : 'auto',
          left: width >= 768 ? '50%' : 'auto'
        }}
      >
          <div className="p-2 space-y-2">
            <button
              onClick={() => handleNotificationClick('weekly-vote')}
              className="relative btn-textured-secondary p-3 animate-fadeIn opacity-0 transition-all duration-200 cursor-pointer w-full text-left overflow-hidden"
              style={{
                animationDelay: '0.02s',
                animationFillMode: 'forwards',
                maxHeight: expandedNotification === 'weekly-vote' ? '200px' : '100px',
                transition: 'max-height 0.25s ease-in-out'
              }}
            >
              <div className="flex items-start gap-3 w-full">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-300 mt-0.5 flex-shrink-0">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12,6 12,12 16,14"></polyline>
                </svg>
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2 w-full">
                    <h4 className="font-semibold text-white text-sm flex-1">{showVotingResults ? 'Weekly Vote Ended' : 'Weekly Vote Started'}</h4>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-red-dot-pulse"></div>
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`text-purple-300 transition-transform duration-200 ${expandedNotification === 'weekly-vote' ? 'rotate-90' : ''}`}>
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </div>
                  </div>
                  <p className="text-sm text-purple-200 leading-relaxed w-full">
                    {expandedNotification === 'weekly-vote'
                      ? <>
                          {showVotingResults
                            ? <>Weekly Vote has ended! The results are now available. See which game won this week's voting.<br /><strong className="font-bold text-purple-100">Click to view results</strong></>
                            : <>Voting for this week's game has begun in Weekly Vote. Cast your vote and help decide what game we'll play next!<br /><strong className="font-bold text-purple-100">Click to vote</strong></>
                          }
                        </>
                      : showVotingResults
                        ? "Weekly Vote has ended! Results are available..."
                        : "Voting for this week's game has begun..."}
                  </p>
                </div>
              </div>
            </button>
            {/* <button
              onClick={() => handleNotificationClick('tweet-contest')}
              className="relative btn-textured-secondary p-3 animate-fadeIn opacity-0 transition-all duration-200 cursor-pointer w-full text-left overflow-hidden"
              style={{
                animationDelay: '0.04s',
                animationFillMode: 'forwards',
                maxHeight: expandedNotification === 'tweet-contest' ? '200px' : '100px',
                transition: 'max-height 0.25s ease-in-out'
              }}
            >
              <div className="flex items-start gap-3 w-full">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 30 30" fill="currentColor" className="text-purple-300 mt-0.5 flex-shrink-0">
                <path d="M26.37,26l-8.795-12.822l0.015,0.012L25.52,4h-2.65l-6.46,7.48L11.28,4H4.33l8.211,11.971L12.54,15.97L3.88,26h2.65 l7.182-8.322L19.42,26H26.37z M10.23,6l12.34,18h-2.1L8.12,6H10.23z"></path>
              </svg>
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2 w-full">
                    <h4 className="font-semibold text-white text-sm flex-1">Contest Results</h4>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-red-dot-pulse"></div>
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`text-purple-300 transition-transform duration-200 ${expandedNotification === 'tweet-contest' ? 'rotate-90' : ''}`}>
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </div>
                  </div>
                  <p className="text-sm text-purple-200 leading-relaxed w-full">
                    {expandedNotification === 'tweet-contest'
                      ? <>Congratulations to the winners. Your threads did exactly what we hoped.<br /><strong className="font-bold text-purple-100">Click to find out more</strong></>
                      : "Congratulations to the winners..."}
                  </p>
                </div>
              </div>
            </button> */}
          </div>
        </div>

      <VotingModal
        isOpen={isVotingModalOpen}
        onClose={handleVotingModalClose}
        showResults={showVotingResults}
      />
    </div>
  );
}
